import { Link } from 'react-router-dom';

import { Icon } from '@/components/icon';
import { Reveal } from '@/components/reveal';
import { Section } from '@/components/section';
import { categoryMeta, type ServiceCategory } from '@/data/services';

import { ServiceIllustration } from './service-illustration';

interface CategoryEntry {
  key: ServiceCategory;
  number: string;
  teaser: string;
}

const categories: CategoryEntry[] = [
  {
    key: 'baumpflege',
    number: '01',
    teaser: 'Kronenpflege, Totholzentfernung, Sturmschäden — fachgerecht nach FLL.',
  },
  {
    key: 'baumhausbau',
    number: '02',
    teaser: 'Familienprojekte und Glamping-Plattformen, baumschonend befestigt.',
  },
  {
    key: 'baumfaellung',
    number: '03',
    teaser: 'Auch Notfälle, auch über Bebauung, auch in engen Innenhöfen.',
  },
];

export function ServiceOverview(): React.ReactNode {
  return (
    <Section background="bone" spacing="loose">
      <Reveal>
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-moss">
          Was wir tun
        </p>
        <h2 className="mb-16 max-w-3xl font-display text-[clamp(2.5rem,5vw,4rem)] font-semibold leading-[1] tracking-tight">
          Drei Disziplinen.
          <br />
          <span className="text-mute">Ein Anspruch.</span>
        </h2>
      </Reveal>

      <ul className="divide-y divide-bark/10 border-y border-bark/10">
        {categories.map((entry) => {
          const meta = categoryMeta[entry.key];
          return (
            <li key={entry.key}>
              <Link
                to={`/${entry.key}`}
                className="group relative flex items-center gap-4 overflow-hidden py-6 sm:gap-8 sm:py-10"
              >
                {/* Animated illustration — visible on all screens, just smaller on mobile */}
                <div className="shrink-0">
                  <ServiceIllustration
                    category={entry.key}
                    className="h-16 w-16 sm:h-24 sm:w-24 lg:h-28 lg:w-28"
                  />
                </div>

                <div className="flex flex-1 flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-10">
                  <span className="font-display text-[10px] uppercase tracking-[0.3em] text-mute sm:w-12 sm:text-xs">
                    {entry.number}
                  </span>
                  <h3 className="font-display text-[clamp(1.5rem,5vw,3.5rem)] font-semibold leading-[1.05] tracking-tight transition-colors group-hover:text-moss">
                    {meta.title}
                  </h3>
                  <p className="hidden text-sm text-mute sm:block sm:max-w-md sm:flex-1">
                    {entry.teaser}
                  </p>
                </div>

                <span className="hidden shrink-0 items-center gap-2 text-sm font-medium text-bark transition-all duration-300 group-hover:gap-3 sm:inline-flex">
                  <span className="opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    Ansehen
                  </span>
                  <Icon name="ArrowRight" className="h-5 w-5" />
                </span>
                <Icon name="ArrowRight" className="h-4 w-4 shrink-0 text-mute sm:hidden" />

                <span
                  className="pointer-events-none absolute bottom-0 left-0 right-0 h-px origin-left scale-x-0 bg-bark transition-transform duration-500 group-hover:scale-x-100"
                  aria-hidden="true"
                />
              </Link>
              <p className="-mt-3 mb-6 pl-20 text-xs text-mute sm:hidden">{entry.teaser}</p>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
